import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { ArrowLeft, FileText, Plus, Printer, User, Wallet } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useConfirmSaveModal } from '@/components/common/Modals'
import { toast } from '@/components/common/Toast'
import { Bill, type BillExtraCharge } from '@/models/bill/bill'
import {
  printBillPdf,
  useCreateBillPaymentRound,
  useUpdateBill,
} from '@/modules/bill/services/bill'
import { cn } from '@/lib/utils'
import { BILL_LOCATION_LABELS, type BillLocationValue } from '@/constants/bill-location'
import { formatNumber } from '@/utils/number'

import { BillInstallmentPdfPanel } from './BillInstallmentPdfPanel'
import type { BillPdfHandle } from './BillPDF'

type BillViewProps = {
  data: Bill | undefined
  type?: 'pending' | 'all'
  tab?: string
  q?: string
}

function DetailRow({
  label,
  value,
  valueClassName,
}: {
  label: string
  value: ReactNode
  valueClassName?: string
}) {
  return (
    <div
      className={cn(
        'grid grid-cols-1 gap-1 border-b border-border/70 py-2.5 last:border-b-0 sm:grid-cols-[minmax(0,7.5rem)_1fr] sm:gap-x-4',
      )}
    >
      <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:pt-0.5">{label}</dt>
      <dd className={cn('break-words text-sm font-medium leading-relaxed text-foreground', valueClassName)}>{value}</dd>
    </div>
  )
}

function paymentStatusLabel(status: string | undefined) {
  if (status === 'paid') return 'ชำระครบแล้ว'
  if (status === 'partial') return 'ชำระบางส่วน'
  return 'ยังไม่ชำระ'
}

function formatExtraChargeLine(row: BillExtraCharge) {
  const name = row.expense_type_name ?? 'ค่าใช้จ่ายเพิ่มเติม'
  const amount = Number(row.amount_baht) || 0
  const qty = row.quantity != null ? Number(row.quantity) : undefined
  const price = row.unit_price_baht != null ? Number(row.unit_price_baht) : undefined
  if (qty != null && price != null && qty >= 1 && price > 0) {
    return `${name} — ${formatNumber(qty)} × ${formatNumber(price)} = ${formatNumber(amount)} บาท`
  }
  return `${name} — ${formatNumber(amount)} บาท`
}

function paymentStatusBadgeClass(status: string | undefined) {
  if (status === 'paid') return 'bg-primary/12 text-primary'
  if (status === 'partial') return 'bg-amber-500/15 text-amber-800 dark:text-amber-200'
  return 'bg-muted text-muted-foreground'
}

export const BillView = ({ data, type = 'all', tab = 'buy', q = '' }: BillViewProps) => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const pdfRef = useRef<BillPdfHandle>(null)
  const { ConfirmSaveModal, showConfirmSaveModal } = useConfirmSaveModal()

  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false)
  const [paidAmountInput, setPaidAmountInput] = useState('')
  const [paymentMethodInput, setPaymentMethodInput] = useState<'cash' | 'transfer'>('cash')
  const [paymentNote, setPaymentNote] = useState('')
  const [pdfRoundNo, setPdfRoundNo] = useState<number | undefined>(undefined)

  const $apiUpdateBill = useUpdateBill(data?.id ?? '')
  const $apiCreatePayment = useCreateBillPaymentRound(data?.id ?? '')

  const patchBase = () => ({
    bill_date: data?.bill_date ?? '',
    location: data?.location ?? 'chanthaburi',
    customer_information: {
      name: data?.customer_information.name ?? '',
      phone: data?.customer_information.phone ?? '',
      car_registration: data?.customer_information.car_registration ?? '',
      address: data?.customer_information.address ?? '',
      tax_id: data?.customer_information.tax_id ?? '',
      brand_mark: data?.customer_information.brand_mark ?? '',
    },
    order_information: data?.order_information ?? [],
    bill_information: {
      bill_type: data?.bill_information.bill_type ?? 'buy',
      sale_mode: data?.bill_information.sale_mode ?? 'retail',
      buy_party_type: data?.bill_information.buy_party_type,
      container_no: data?.bill_information.container_no,
      customer_sequence_no: data?.bill_information.customer_sequence_no,
      discount_baht: Number(data?.bill_information.discount_baht ?? 0),
      extra_charges: data?.bill_information.extra_charges ?? [],
    },
  })

  const handlePrintBill = async () => {
    try {
      await $apiUpdateBill.mutateAsync({
        ...patchBase(),
        payment_information: {
          payment_method: data?.payment_information.payment_method ?? '',
          payment_status: data?.payment_information.payment_status ?? '',
        },
        print_status: 'printed',
      })
      toast.toastSuccess('พิมพ์ใบเสร็จแล้ว')
    } catch (e) {
      if (e instanceof Error) {
        toast.toastFailed(e.message)
      }
    }
  }

  const handleAddPaymentRound = async () => {
    const amount = Number(paidAmountInput)
    const remaining = data?.remaining_amount ?? data?.payment_information?.remaining_amount ?? 0
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.toastFailed('กรุณากรอกยอดชำระที่มากกว่า 0')
      return
    }
    if (amount > remaining + 0.01) {
      toast.toastFailed(`ยอดชำระเกินยอดค้าง (คงเหลือ ${formatNumber(remaining)} บาท)`)
      return
    }
    try {
      const res = await $apiCreatePayment.mutateAsync({
        paid_amount: amount,
        payment_method: paymentMethodInput,
        note: paymentNote.trim() || undefined,
      })
      const payload = res as { data?: Bill } & Bill
      const billPayload = payload.data ?? payload
      const createdRound =
        billPayload.created_payment_round ??
        billPayload.payment_rounds?.[billPayload.payment_rounds.length - 1]?.round_no

      await queryClient.invalidateQueries({ queryKey: ['bills', data?.id] })

      toast.toastSuccess('บันทึกการชำระงวดแล้ว')
      setPaymentDialogOpen(false)
      setPaidAmountInput('')
      setPaymentNote('')

      if (createdRound != null) {
        setPdfRoundNo(createdRound)
        if (data?.id) {
          await printBillPdf(data.id, createdRound)
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        toast.toastFailed(e.message)
      }
    }
  }

  const openPaymentDialog = () => {
    const remaining = data?.remaining_amount ?? data?.payment_information?.remaining_amount ?? 0
    setPaidAmountInput(remaining > 0 ? String(remaining) : '')
    setPaymentMethodInput(
      (data?.payment_information?.payment_method === 'transfer' ? 'transfer' : 'cash') as 'cash' | 'transfer',
    )
    setPaymentNote('')
    setPaymentDialogOpen(true)
  }

  const goBack = () => {
    if (type === 'pending') {
      navigate({ to: '/admin/outstanding', search: { tab, q: q || undefined } })
      return
    }
    window.history.back()
  }

  const paymentRounds = data?.payment_rounds
  const sortedRounds = useMemo(
    () => [...(paymentRounds ?? [])].sort((a, b) => a.round_no - b.round_no),
    [paymentRounds],
  )
  const latestRound =
    sortedRounds.length > 0 ? sortedRounds[sortedRounds.length - 1].round_no : undefined

  useEffect(() => {
    if (!data?.id) return
    if (latestRound != null) {
      setPdfRoundNo((prev) =>
        prev != null && sortedRounds.some((r) => r.round_no === prev) ? prev : latestRound,
      )
    } else {
      setPdfRoundNo(undefined)
    }
  }, [data?.id, latestRound, sortedRounds])

  if (!data) return null

  const dash = (s: string | undefined | null) => (s && String(s).trim() !== '' ? s : '—')
  const totalAmount = data.total_amount ?? 0
  const paidAmount = data.paid_amount ?? data.payment_information?.total_paid ?? 0
  const remainingAmount =
    data.remaining_amount ?? data.payment_information?.remaining_amount ?? Math.max(0, totalAmount - paidAmount)
  const paymentStatus = data.payment_information?.payment_status ?? 'pending'
  const fruitSubtotal =
    data.line_items?.length
      ? data.line_items.reduce((sum, row) => sum + (Number(row.line_total) || 0), 0)
      : data.order_information?.reduce(
          (sum, row) =>
            sum +
            (row.cycles?.reduce((cyclesSum, cycle) => cyclesSum + (Number(cycle.product_quantity) || 0), 0) ?? 0) *
              (Number(row.product_price) || 0),
          0,
        ) ?? 0
  const extraChargesFromList = data.bill_information?.extra_charges ?? []
  const extraChargesFromLegacy = [
    {
      expense_type_name: 'ค่ากล่อง',
      amount_baht: Number(data.bill_information?.box_fee_baht ?? 0),
    },
    {
      expense_type_name: 'สติกเกอร์',
      amount_baht: Number(data.bill_information?.sticker_fee_baht ?? 0),
    },
  ].filter((row) => row.amount_baht > 0)
  const extraCharges = [...extraChargesFromList, ...extraChargesFromLegacy]
  const extraChargeTotal = extraCharges.reduce((sum, row) => sum + (Number(row.amount_baht) || 0), 0)
  const discountBaht = Number(data.bill_information?.discount_baht ?? 0)
  const canAddPayment = remainingAmount > 0.01
  const rounds = sortedRounds
  const activePdfRound = pdfRoundNo ?? latestRound

  return (
    <div className="w-full space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
        <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={goBack}>
          <ArrowLeft className="mr-2 h-4 w-4 shrink-0" aria-hidden />
          กลับ
        </Button>
        {canAddPayment ? (
          <Button type="button" variant="default" className="w-full sm:w-auto" onClick={openPaymentDialog}>
            <Wallet className="mr-2 h-4 w-4 shrink-0" aria-hidden />
            ชำระเงิน
          </Button>
        ) : null}
        <Button
          type="button"
          variant={canAddPayment ? 'outline' : 'default'}
          className="w-full sm:w-auto"
          onClick={() => void pdfRef.current?.print()}
        >
          <Printer className="mr-2 h-4 w-4 shrink-0" aria-hidden />
          {activePdfRound != null ? `พิมพ์ใบเสร็จงวดที่ ${activePdfRound}` : 'สั่งพิมพ์'}
        </Button>
      </div>

      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(280px,380px)_1fr] xl:grid-cols-[400px_1fr] xl:gap-8">
        <aside className="flex min-w-0 flex-col gap-4 lg:sticky lg:top-20">
          <Card className="overflow-hidden border-border/90 shadow-sm">
            <CardHeader className="flex flex-row items-center gap-3 space-y-0 border-b border-border/80 bg-muted/25 py-3.5">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/12 text-primary">
                <FileText className="h-4 w-4" aria-hidden />
              </div>
              <CardTitle className="text-base font-semibold leading-tight">ข้อมูลใบเสร็จ</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-1 pt-0 sm:px-5">
              <dl>
                <DetailRow label="วันที่บิล" value={data.bill_date || '—'} />
                <DetailRow
                  label="สถานที่"
                  value={
                    data.location
                      ? BILL_LOCATION_LABELS[data.location as BillLocationValue] ?? data.location
                      : '—'
                  }
                />
                <DetailRow
                  label="ประเภทบิล"
                  value={data.bill_information.bill_type === 'buy' ? 'ซื้อ' : 'ขาย'}
                />
                <DetailRow
                  label="เลขใบเสร็จ"
                  value={data.bill_number || '—'}
                  valueClassName="font-mono text-primary"
                />
              </dl>
            </CardContent>
          </Card>

          <Card className="overflow-hidden border-border/90 shadow-sm">
            <CardHeader className="flex flex-row items-center gap-3 space-y-0 border-b border-border/80 bg-muted/25 py-3.5">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/12 text-primary">
                <User className="h-4 w-4" aria-hidden />
              </div>
              <CardTitle className="text-base font-semibold leading-tight">ข้อมูลผู้ซื้อ</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-1 pt-0 sm:px-5">
              <dl>
                <DetailRow label="ชื่อ" value={dash(data.customer_information.name)} />
                <DetailRow label="เบอร์โทร" value={dash(data.customer_information.phone)} />
                <DetailRow label="ทะเบียนรถ" value={dash(data.customer_information.car_registration)} />
              </dl>
            </CardContent>
          </Card>

          <Card className="overflow-hidden border-border/90 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 border-b border-border/80 bg-muted/25 py-3.5">
              <div className="flex flex-row items-center gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/12 text-primary">
                  <Wallet className="h-4 w-4" aria-hidden />
                </div>
                <CardTitle className="text-base font-semibold leading-tight">ข้อมูลการชำระ</CardTitle>
              </div>
              {canAddPayment ? (
                <Button type="button" size="sm" variant="outline" onClick={openPaymentDialog}>
                  <Plus className="mr-1 h-3.5 w-3.5" aria-hidden />
                  เพิ่มงวด
                </Button>
              ) : null}
            </CardHeader>
            <CardContent className="space-y-4 px-4 pb-4 pt-2 sm:px-5">
              <dl>
                <DetailRow label="ยอดทุเรียน" value={`${formatNumber(fruitSubtotal)} บาท`} />
                {extraCharges.length > 0 ? (
                  <DetailRow
                    label="ค่าใช้จ่ายเพิ่ม"
                    value={
                      <div className="space-y-1">
                        {extraCharges.map((row, idx) => (
                          <div key={`${row.expense_type_name ?? 'extra'}-${idx}`}>
                            {formatExtraChargeLine(row)}
                          </div>
                        ))}
                      </div>
                    }
                  />
                ) : null}
                <DetailRow label="รวมค่าใช้จ่ายเพิ่ม" value={`${formatNumber(extraChargeTotal)} บาท`} />
                <DetailRow label="ส่วนลด" value={`${formatNumber(discountBaht)} บาท`} />
                <DetailRow label="ยอดรวมบิล" value={`${formatNumber(totalAmount)} บาท`} />
                <DetailRow label="ชำระแล้ว" value={`${formatNumber(paidAmount)} บาท`} />
                <DetailRow
                  label="ยอดค้าง"
                  value={`${formatNumber(remainingAmount)} บาท`}
                  valueClassName={remainingAmount > 0 ? 'text-amber-700 dark:text-amber-300' : undefined}
                />
                <DetailRow
                  label="สถานะการชำระ"
                  value={
                    <span
                      className={cn(
                        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
                        paymentStatusBadgeClass(paymentStatus),
                      )}
                    >
                      {paymentStatusLabel(paymentStatus)}
                    </span>
                  }
                />
              </dl>

              {rounds.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    ประวัติการชำระ ({rounds.length} งวด)
                  </p>
                  <ul className="space-y-1.5">
                    {rounds.map((r) => {
                      const isActive = activePdfRound === r.round_no
                      return (
                      <li key={r.id}>
                          <button
                            type="button"
                            onClick={() => setPdfRoundNo(r.round_no)}
                            className={cn(
                              'w-full rounded-md border px-3 py-2 text-left text-sm transition-colors',
                              isActive
                                ? 'border-primary bg-primary/10 ring-1 ring-primary/30'
                                : 'border-border/60 bg-background hover:bg-muted/50',
                            )}
                          >
                        <div className="flex items-center justify-between gap-2 font-medium">
                          <span>งวดที่ {r.round_no}</span>
                          <span className={isActive ? 'text-primary' : 'text-foreground'}>
                            {formatNumber(r.paid_amount)} บาท
                          </span>
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {r.paid_at?.slice(0, 16).replace('T', ' ')} ·{' '}
                          {r.payment_method === 'cash' ? 'เงินสด' : 'โอน'} · คงเหลือ{' '}
                          {formatNumber(r.remaining_after)} บาท
                        </p>
                        {r.note ? <p className="mt-0.5 text-xs text-muted-foreground">{r.note}</p> : null}
                            {isActive ? (
                              <p className="mt-1.5 text-xs font-medium text-primary">กำลังแสดงใบเสร็จ →</p>
                            ) : null}
                          </button>
                      </li>
                      )
                    })}
                  </ul>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">ยังไม่มีประวัติการชำระงวด</p>
              )}
            </CardContent>
          </Card>
        </aside>

        <section className="min-w-0">
          <BillInstallmentPdfPanel
            ref={pdfRef}
            data={data}
            handlePrintBill={handlePrintBill}
            selectedRoundNo={pdfRoundNo}
            onSelectedRoundChange={setPdfRoundNo}
          />
        </section>
      </div>

      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>บันทึกการชำระเงิน</DialogTitle>
            <DialogDescription>
              ยอดค้างคงเหลือ {formatNumber(remainingAmount)} บาท — ชำระครบงวดนี้แล้วสถานะจะเป็น
              &quot;ชำระครบแล้ว&quot; อัตโนมัติ
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label htmlFor="paid_amount">ยอดที่ชำระในงวดนี้ (บาท)</Label>
              <Input
                id="paid_amount"
                type="number"
                min={0.01}
                step={0.01}
                max={remainingAmount}
                value={paidAmountInput}
                onChange={(e) => setPaidAmountInput(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label>วิธีการชำระ</Label>
              <Select
                value={paymentMethodInput}
                onValueChange={(v) => setPaymentMethodInput(v as 'cash' | 'transfer')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">เงินสด</SelectItem>
                  <SelectItem value="transfer">โอนผ่านธนาคาร</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="payment_note">หมายเหตุ (ถ้ามี)</Label>
              <Input
                id="payment_note"
                value={paymentNote}
                onChange={(e) => setPaymentNote(e.target.value)}
                placeholder="เช่น รับชำระจากลูกค้าปลายทาง"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setPaymentDialogOpen(false)}>
              ยกเลิก
            </Button>
            <Button
              type="button"
              disabled={$apiCreatePayment.isLoading}
              onClick={() => showConfirmSaveModal()}
            >
              บันทึกการชำระ
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmSaveModal
        title="ยืนยันการชำระเงิน"
        description={`ชำระ ${formatNumber(Number(paidAmountInput) || 0)} บาท ในงวดนี้`}
        onConfirm={handleAddPaymentRound}
      />
    </div>
  )
}
